import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { Box, Chip, IconButton, Stack, TextField, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { MButton } from "../../../../components/button";
import { MInput } from "../../../../components/form/FormInput";
import { BouncingDots } from "../../../../components/loader";
import { customToast } from "../../../../components/notify";
import {
    useGetDiningOptionsQuery,
    useGetFeatureRecommendationSettingQuery,
    useGetOrderFlowSettingQuery,
    useUpdateDiningOptionsMutation,
    useUpdateFeatureRecommendationSettingMutation,
    useUpdateOrderFlowSettingMutation,
} from "../../services/storeApiSlice";
import styles from "./CardSettings.module.scss";

interface CardSettingsProps {
    storeId: string;
}

interface CardSettingsState {
    paymentMethods: {
        creditCard: boolean;
        cashPayment: boolean;
        onlinePayment: boolean;
        linePay: boolean;
        noBindingDemo: boolean;
    };
    diningOptions: {
        dineIn: boolean;
        takeOut: boolean;
    };
    otherSettings: {
        useOrgCorrector: boolean;
        useTagRecommend: boolean;
        enableWebLogin: boolean;
        prompt: string;
    };
    chatMode: "featured_recommendations" | "order_flow";
    chatModeVersion: "v1" | "v2";
    orderFlowSettings: {
        orderDistinguishConfusedItems: boolean;
        specificationEnhanceMatch: boolean;
        announceProductOnAddToCart: boolean;
    };
    featureRecommendationSettings: {
        knowledgeTypes: string[];
        itemTypes: string[];
        useAccurateKnowledgePopup: boolean;
        embeddingSearchLimit: number;
        fulltextSearchLimit: number;
        embeddingSearchMinScore: number;
        fulltextSearchMinScore: number;
        tagrecommendLimit: number;
    };
}

const CardSettings: React.FC<CardSettingsProps> = ({ storeId }) => {
    const { t } = useTranslation();

    // RTK Query hooks
    const {
        data: diningOptionsData,
        isLoading: isLoadingSettings,
        error,
    } = useGetDiningOptionsQuery(storeId ? { store_id: storeId } : skipToken);
    const { data: orderFlowData } = useGetOrderFlowSettingQuery(storeId ? { store_id: storeId } : skipToken);
    const { data: featureRecommendationData } = useGetFeatureRecommendationSettingQuery(
        storeId ? { store_id: storeId } : skipToken
    );
    const [updateDiningOptions, { isLoading: isUpdatingDining }] = useUpdateDiningOptionsMutation();
    const [updateOrderFlow, { isLoading: isUpdatingOrderFlow }] = useUpdateOrderFlowSettingMutation();
    const [updateFeatureRecommendation, { isLoading: isUpdatingFeatureRecommendation }] =
        useUpdateFeatureRecommendationSettingMutation();

    // 初始設定：每組至少有一個選項開啟
    const [settings, setSettings] = useState<CardSettingsState>({
        paymentMethods: {
            creditCard: true,
            cashPayment: true,
            onlinePayment: false,
            linePay: false,
            noBindingDemo: false,
        },
        diningOptions: {
            dineIn: true,
            takeOut: false,
        },
        otherSettings: {
            useOrgCorrector: false,
            useTagRecommend: false,
            enableWebLogin: false,
            prompt: "",
        },
        chatMode: "order_flow",
        chatModeVersion: "v1",
        orderFlowSettings: {
            orderDistinguishConfusedItems: false,
            specificationEnhanceMatch: false,
            announceProductOnAddToCart: false,
        },
        featureRecommendationSettings: {
            knowledgeTypes: [],
            itemTypes: [],
            useAccurateKnowledgePopup: false,
            embeddingSearchLimit: 5,
            fulltextSearchLimit: 5,
            embeddingSearchMinScore: 0,
            fulltextSearchMinScore: 0,
            tagrecommendLimit: 15,
        },
    });

    // 當從 API 獲取資料時更新狀態
    useEffect(() => {
        if (diningOptionsData?.data) {
            const {
                available_dining_options,
                available_payment_methods,
                chat_mode,
                chat_mode_version,
                use_org_corrector,
                use_tag_recommend,
                enable_web_login,
                prompt,
            } = diningOptionsData.data;

            setSettings((prev) => ({
                ...prev,
                diningOptions: {
                    dineIn: available_dining_options?.includes("dine_in") ?? prev.diningOptions.dineIn,
                    takeOut: available_dining_options?.includes("takeout") ?? prev.diningOptions.takeOut,
                },
                paymentMethods: {
                    creditCard: available_payment_methods?.includes("credit_card") ?? prev.paymentMethods.creditCard,
                    cashPayment: available_payment_methods?.includes("on_site") ?? prev.paymentMethods.cashPayment,
                    onlinePayment: available_payment_methods?.includes("online") ?? prev.paymentMethods.onlinePayment,
                    linePay: available_payment_methods?.includes("line_pay") ?? prev.paymentMethods.linePay,
                    noBindingDemo:
                        available_payment_methods?.includes("no_binding_demo") ?? prev.paymentMethods.noBindingDemo,
                },
                otherSettings: {
                    useOrgCorrector: use_org_corrector ?? prev.otherSettings.useOrgCorrector,
                    useTagRecommend: use_tag_recommend ?? prev.otherSettings.useTagRecommend,
                    enableWebLogin: enable_web_login ?? prev.otherSettings.enableWebLogin,
                    prompt: prompt ?? prev.otherSettings.prompt,
                },
                chatMode: (chat_mode as "featured_recommendations" | "order_flow") ?? prev.chatMode,
                chatModeVersion: (chat_mode_version as "v1" | "v2") ?? prev.chatModeVersion,
            }));
        }
    }, [diningOptionsData]);

    // 當從 API 獲取 Order Flow 資料時更新狀態
    useEffect(() => {
        if (orderFlowData?.data) {
            const { order_distinguish_confused_items, specification_enhance_match, announce_product_on_add_to_cart } =
                orderFlowData.data;

            setSettings((prev) => ({
                ...prev,
                orderFlowSettings: {
                    orderDistinguishConfusedItems:
                        order_distinguish_confused_items ?? prev.orderFlowSettings.orderDistinguishConfusedItems,
                    specificationEnhanceMatch:
                        specification_enhance_match ?? prev.orderFlowSettings.specificationEnhanceMatch,
                    announceProductOnAddToCart:
                        announce_product_on_add_to_cart ?? prev.orderFlowSettings.announceProductOnAddToCart,
                },
            }));
        }
    }, [orderFlowData]);

    // 當從 API 獲取 Feature Recommendation 資料時更新狀態
    useEffect(() => {
        if (featureRecommendationData?.data) {
            const {
                item_type,
                knowledge_type,
                use_accurate_knowledge_popup,
                embedding_search_limit,
                fulltext_search_limit,
                embedding_search_min_score,
                fulltext_search_min_score,
                tagrecommend_limit,
            } = featureRecommendationData.data;

            setSettings((prev) => ({
                ...prev,
                featureRecommendationSettings: {
                    knowledgeTypes: knowledge_type ?? prev.featureRecommendationSettings.knowledgeTypes,
                    itemTypes: item_type ?? prev.featureRecommendationSettings.itemTypes,
                    useAccurateKnowledgePopup:
                        use_accurate_knowledge_popup ?? prev.featureRecommendationSettings.useAccurateKnowledgePopup,
                    embeddingSearchLimit:
                        embedding_search_limit ?? prev.featureRecommendationSettings.embeddingSearchLimit,
                    fulltextSearchLimit:
                        fulltext_search_limit ?? prev.featureRecommendationSettings.fulltextSearchLimit,
                    embeddingSearchMinScore:
                        embedding_search_min_score ?? prev.featureRecommendationSettings.embeddingSearchMinScore,
                    fulltextSearchMinScore:
                        fulltext_search_min_score ?? prev.featureRecommendationSettings.fulltextSearchMinScore,
                    tagrecommendLimit: tagrecommend_limit ?? prev.featureRecommendationSettings.tagrecommendLimit,
                },
            }));
        }
    }, [featureRecommendationData]);

    // 檢查某個群組是否只有一個選項開啟
    // const isOnlyOptionEnabled = (group: keyof CardSettingsState, option: string): boolean => {
    //     const groupSettings = settings[group];
    //     const enabledCount = Object.values(groupSettings).filter(Boolean).length;
    //     return enabledCount === 1 && groupSettings[option as keyof typeof groupSettings];
    // };

    // 處理開關變更
    // const handleToggle = (group: keyof CardSettingsState, option: string) => {
    //     // 如果這是該群組中唯一開啟的選項，則不允許關閉
    //     if (isOnlyOptionEnabled(group, option)) {
    //         return;
    //     }

    //     setSettings((prev) => {
    //         if (group === "paymentMethods") {
    //             return {
    //                 ...prev,
    //                 paymentMethods: {
    //                     ...prev.paymentMethods,
    //                     [option]: !prev.paymentMethods[option as keyof typeof prev.paymentMethods],
    //                 },
    //             };
    //         } else if (group === "diningOptions") {
    //             return {
    //                 ...prev,
    //                 diningOptions: {
    //                     ...prev.diningOptions,
    //                     [option]: !prev.diningOptions[option as keyof typeof prev.diningOptions],
    //                 },
    //             };
    //         } else if (group === "otherSettings") {
    //             return {
    //                 ...prev,
    //                 otherSettings: {
    //                     ...prev.otherSettings,
    //                     [option]: !prev.otherSettings[option as keyof typeof prev.otherSettings],
    //                 },
    //             };
    //         }
    //         return prev;
    //     });
    // };

    // 處理儲存
    const handleSave = async () => {
        if (!storeId) {
            customToast.error(t("SALES.StoreIdNotFound") || "Store ID not found");
            return;
        }

        // 只在點餐模式下驗證用餐方式和付款方式
        if (settings.chatMode === "order_flow") {
            // 確保至少有一個用餐方式被選中
            const { dineIn, takeOut } = settings.diningOptions;
            if (!dineIn && !takeOut) {
                customToast.error(t("SALES.AtLeastOneDiningOption") || "At least one dining option is required");
                return;
            }

            // 確保至少有一個付款方式被選中
            const { creditCard, cashPayment, onlinePayment, linePay, noBindingDemo } = settings.paymentMethods;
            if (!creditCard && !cashPayment && !onlinePayment && !linePay && !noBindingDemo) {
                customToast.error(t("SALES.AtLeastOnePaymentMethod") || "At least one payment method is required");
                return;
            }
        }

        try {
            // 準備基本設定（所有模式都需要）
            const baseSettings = {
                store_id: storeId,
                chat_mode: settings.chatMode,
                chat_mode_version: settings.chatModeVersion,
                use_org_corrector: settings.otherSettings.useOrgCorrector,
                use_tag_recommend: settings.otherSettings.useTagRecommend,
                enable_web_login: settings.otherSettings.enableWebLogin,
                prompt: settings.otherSettings.prompt,
            };

            // 只在點餐模式下處理 diningOptions 和 paymentMethods
            if (settings.chatMode === "order_flow") {
                // 將 diningOptions 轉換為 API 需要的格式
                const available_dining_options: string[] = [];
                if (settings.diningOptions.dineIn) {
                    available_dining_options.push("dine_in");
                }
                if (settings.diningOptions.takeOut) {
                    available_dining_options.push("takeout");
                }

                // 將 paymentMethods 轉換為 API 需要的格式
                const available_payment_methods: string[] = [];
                if (settings.paymentMethods.creditCard) {
                    available_payment_methods.push("credit_card");
                }
                if (settings.paymentMethods.cashPayment) {
                    available_payment_methods.push("on_site");
                }
                if (settings.paymentMethods.onlinePayment) {
                    available_payment_methods.push("online");
                }
                if (settings.paymentMethods.linePay) {
                    available_payment_methods.push("line_pay");
                }
                if (settings.paymentMethods.noBindingDemo) {
                    available_payment_methods.push("no_binding_demo");
                }

                // 呼叫 API 更新設定（包含 dining 和 payment）
                await updateDiningOptions({
                    ...baseSettings,
                    available_dining_options,
                    available_payment_methods,
                }).unwrap();
            } else {
                // 展場模式：只更新基本設定，不包含 dining 和 payment
                await updateDiningOptions(baseSettings).unwrap();
            }

            // 根據 chatMode 更新對應的設定
            if (settings.chatMode === "order_flow") {
                // 更新點餐模式設定
                await updateOrderFlow({
                    store_id: storeId,
                    order_distinguish_confused_items: settings.orderFlowSettings.orderDistinguishConfusedItems,
                    specification_enhance_match: settings.orderFlowSettings.specificationEnhanceMatch,
                    announce_product_on_add_to_cart: settings.orderFlowSettings.announceProductOnAddToCart,
                }).unwrap();
            } else if (settings.chatMode === "featured_recommendations") {
                // 更新展場模式設定
                await updateFeatureRecommendation({
                    store_id: storeId,
                    item_type: settings.featureRecommendationSettings.itemTypes,
                    knowledge_type: settings.featureRecommendationSettings.knowledgeTypes,
                    use_accurate_knowledge_popup: settings.featureRecommendationSettings.useAccurateKnowledgePopup,
                    embedding_search_limit: settings.featureRecommendationSettings.embeddingSearchLimit,
                    fulltext_search_limit: settings.featureRecommendationSettings.fulltextSearchLimit,
                    embedding_search_min_score: settings.featureRecommendationSettings.embeddingSearchMinScore,
                    fulltext_search_min_score: settings.featureRecommendationSettings.fulltextSearchMinScore,
                    tagrecommend_limit: settings.featureRecommendationSettings.tagrecommendLimit,
                }).unwrap();
            }

            customToast.success(t("SALES.SettingsSavedSuccessfully") || "Settings saved successfully");
        } catch (err) {
            customToast.error(t("SALES.FailedToSaveSettings") || "Failed to save settings");
        }
    };

    // Tag input states
    const [knowledgeTypeInput, setKnowledgeTypeInput] = useState("");
    const [itemTypeInput, setItemTypeInput] = useState("");

    // 處理新增知識類型標籤
    const handleAddKnowledgeType = () => {
        if (!knowledgeTypeInput.trim()) return;
        const newType = knowledgeTypeInput.trim();
        if (!settings.featureRecommendationSettings.knowledgeTypes.includes(newType)) {
            setSettings((prev) => ({
                ...prev,
                featureRecommendationSettings: {
                    ...prev.featureRecommendationSettings,
                    knowledgeTypes: [...prev.featureRecommendationSettings.knowledgeTypes, newType],
                },
            }));
        }
        setKnowledgeTypeInput("");
    };

    // 處理刪除知識類型標籤
    const handleDeleteKnowledgeType = (typeToDelete: string) => {
        setSettings((prev) => ({
            ...prev,
            featureRecommendationSettings: {
                ...prev.featureRecommendationSettings,
                knowledgeTypes: prev.featureRecommendationSettings.knowledgeTypes.filter((t) => t !== typeToDelete),
            },
        }));
    };

    // 處理新增商品類型標籤
    const handleAddItemType = () => {
        if (!itemTypeInput.trim()) return;
        const newType = itemTypeInput.trim();
        if (!settings.featureRecommendationSettings.itemTypes.includes(newType)) {
            setSettings((prev) => ({
                ...prev,
                featureRecommendationSettings: {
                    ...prev.featureRecommendationSettings,
                    itemTypes: [...prev.featureRecommendationSettings.itemTypes, newType],
                },
            }));
        }
        setItemTypeInput("");
    };

    // 處理刪除商品類型標籤
    const handleDeleteItemType = (typeToDelete: string) => {
        setSettings((prev) => ({
            ...prev,
            featureRecommendationSettings: {
                ...prev.featureRecommendationSettings,
                itemTypes: prev.featureRecommendationSettings.itemTypes.filter((t) => t !== typeToDelete),
            },
        }));
    };

    // const paymentMethodOptions = [
    //     { key: "creditCard", label: t("SALES.CreditCard") || "Credit Card" },
    //     { key: "cashPayment", label: t("SALES.CashPayment") || "Cash Payment" },
    //     { key: "onlinePayment", label: t("SALES.OnlinePayment") || "Online Payment" },
    //     { key: "linePay", label: t("SALES.LinePay") || "Line Pay" },
    //     { key: "noBindingDemo", label: t("SALES.NoBindingDemo") || "No Binding Demo" },
    // ];

    // const diningOptions = [
    //     { key: "dineIn", label: t("SALES.DineIn") || "Dine In" },
    //     { key: "takeOut", label: t("SALES.TakeOut") || "Take Out" },
    // ];

    // const otherSettingsOptions = [
    //     // { key: "useOrgCorrector", label: t("SALES.UseOrgCorrector") || "Use Organization Corrector" },
    //     { key: "useTagRecommend", label: t("SALES.UseTagRecommend") || "Use Tag Recommendation" },
    //     { key: "enableWebLogin", label: t("SALES.EnableWebLogin") || "Enable Web Login" },
    // ];

    // 處理載入和錯誤狀態
    if (!storeId) {
        return (
            <div className={styles.container}>
                <div className={styles.errorMessage}>
                    {t("SALES.StoreIdNotFound") || "Store ID not found, please enter from the correct path"}
                </div>
            </div>
        );
    }

    if (isLoadingSettings) {
        return (
            <div className={styles.container}>
                <BouncingDots />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.errorMessage}>
                    {t("SALES.FailedToLoadSettings") || "Failed to load settings, please try again later"}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* 其他設定 */}
            <div className={styles.settingsSection}>
                <h2>{t("SALES.StorePrompt") || "Store Prompt"}</h2>

                {/* <div className={styles.optionsList}>
                    {otherSettingsOptions.map((option) => {
                        const value = settings.otherSettings[option.key as keyof typeof settings.otherSettings];
                        const isEnabled = typeof value === "boolean" ? value : false;

                        return (
                            <div key={option.key} className={styles.optionItem}>
                                <span className={styles.optionLabel}>{option.label}</span>
                                <div className={styles.switchContainer}>
                                    <MSwitch
                                        checked={isEnabled}
                                        onChange={() => handleToggle("otherSettings", option.key)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div> */}

                {/* Prompt 輸入欄位 */}
                <div className={styles.promptContainer}>
                    <MInput
                        // label={t("SALES.StorePrompt") || "Store Prompt"}
                        value={settings.otherSettings.prompt}
                        onChange={(e) => {
                            setSettings((prev) => ({
                                ...prev,
                                otherSettings: {
                                    ...prev.otherSettings,
                                    prompt: e.target.value,
                                },
                            }));
                        }}
                        multiline
                        rows={4}
                        variant='outlined'
                        required={false}
                    />
                </div>
            </div>

            {/* 商家模式設定 */}
            {/* <div className={styles.settingsSection}>
                <h2>{t("SALES.ChatMode") || "Store Mode"}</h2>
                <div className={styles.selectContainer}>
                    <MSelect
                        name='chatMode'
                        value={settings.chatMode}
                        onChange={(e) => {
                            const value = e.target.value as "featured_recommendations" | "order_flow";
                            setSettings((prev) => ({
                                ...prev,
                                chatMode: value,
                            }));
                        }}
                        options={[
                            {
                                label: t("SALES.FeaturedRecommendations") || "Exhibition Mode",
                                value: "featured_recommendations",
                            },
                            { label: t("SALES.OrderFlow") || "Order Mode", value: "order_flow" },
                        ]}
                        variant='filled'
                    />
                </div>
            </div> */}

            {/* 點餐模式設定 - 只在點餐模式下顯示 */}
            {settings.chatMode === "order_flow" && (
                <>
                    {/* 對話模式設定 */}
                    {/* <div className={styles.settingsSection}>
                        <h2>{t("SALES.ChatModeVersion") || "對話模式"}</h2>
                        <p className={styles.sectionDescription}>
                            {t("SALES.ChatModeVersionDescription") ||
                                "選擇 AI 的對話風格。「正常」模式適合一般對話互動；「快速點餐」模式將更快速簡潔地完成點餐流程。"}
                        </p>
                        <div className={styles.selectContainer}>
                            <MSelect
                                name='chatModeVersion'
                                value={settings.chatModeVersion}
                                onChange={(e) => {
                                    const value = e.target.value as "v1" | "v2";
                                    setSettings((prev) => ({
                                        ...prev,
                                        chatModeVersion: value,
                                    }));
                                }}
                                options={[
                                    { label: t("SALES.NormalMode") || "正常", value: "v1" },
                                    { label: t("SALES.FastOrderMode") || "快速點餐", value: "v2" },
                                ]}
                                variant='filled'
                            />
                        </div>
                    </div> */}

                    {/* 結帳方式設定 */}
                    {/* <div className={styles.settingsSection}>
                        <h2>{t("SALES.PaymentMethods") || "Payment Methods"}</h2>
                        <p className={styles.sectionDescription}>
                            {t("SALES.PaymentMethodsDescription") ||
                                "Set available payment methods. At least one payment method is required."}
                        </p>
                        <div className={styles.optionsList}>
                            {paymentMethodOptions.map((option) => {
                                const isEnabled =
                                    settings.paymentMethods[option.key as keyof typeof settings.paymentMethods];
                                const isDisabled = isOnlyOptionEnabled("paymentMethods", option.key);

                                return (
                                    <div
                                        key={option.key}
                                        className={`${styles.optionItem} ${isDisabled ? styles.disabled : ""}`}
                                    >
                                        <span className={styles.optionLabel}>{option.label}</span>
                                        <div className={styles.switchContainer}>
                                            <MSwitch
                                                checked={isEnabled}
                                                onChange={() => handleToggle("paymentMethods", option.key)}
                                                disabled={isDisabled}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div> */}

                    {/* 內用外帶方式設定 */}
                    {/* <div className={styles.settingsSection}>
                        <h2>{t("SALES.DiningOptions") || "Dining Options"}</h2>
                        <p className={styles.sectionDescription}>
                            {t("SALES.DiningOptionsDescription") ||
                                "Set available dining options. At least one dining option is required."}
                        </p>
                        <div className={styles.optionsList}>
                            {diningOptions.map((option) => {
                                const isEnabled =
                                    settings.diningOptions[option.key as keyof typeof settings.diningOptions];
                                const isDisabled = isOnlyOptionEnabled("diningOptions", option.key);

                                return (
                                    <div
                                        key={option.key}
                                        className={`${styles.optionItem} ${isDisabled ? styles.disabled : ""}`}
                                    >
                                        <span className={styles.optionLabel}>{option.label}</span>
                                        <div className={styles.switchContainer}>
                                            <MSwitch
                                                checked={isEnabled}
                                                onChange={() => handleToggle("diningOptions", option.key)}
                                                disabled={isDisabled}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div> */}

                    {/* 點餐模式進階設定 */}
                    {/* <div className={styles.settingsSection}>
                        <h2>{t("SALES.OrderFlowSettings") || "點餐模式進階設定"}</h2>
                        <div className={styles.optionsList}>
                            <div className={styles.optionItem}>
                                <span className={styles.optionLabel}>
                                    {t("SALES.OrderDistinguishConfusedItems") || "分辨混淆商品"}
                                </span>
                                <div className={styles.switchContainer}>
                                    <MSwitch
                                        checked={settings.orderFlowSettings.orderDistinguishConfusedItems}
                                        onChange={() => {
                                            setSettings((prev) => ({
                                                ...prev,
                                                orderFlowSettings: {
                                                    ...prev.orderFlowSettings,
                                                    orderDistinguishConfusedItems:
                                                        !prev.orderFlowSettings.orderDistinguishConfusedItems,
                                                },
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={styles.optionItem}>
                                <span className={styles.optionLabel}>
                                    {t("SALES.SpecificationEnhanceMatch") || "規格增強匹配"}
                                </span>
                                <div className={styles.switchContainer}>
                                    <MSwitch
                                        checked={settings.orderFlowSettings.specificationEnhanceMatch}
                                        onChange={() => {
                                            setSettings((prev) => ({
                                                ...prev,
                                                orderFlowSettings: {
                                                    ...prev.orderFlowSettings,
                                                    specificationEnhanceMatch:
                                                        !prev.orderFlowSettings.specificationEnhanceMatch,
                                                },
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={styles.optionItem}>
                                <span className={styles.optionLabel}>
                                    {t("SALES.AnnounceProductOnAddToCart") || "加入購物車時念出商品"}
                                </span>
                                <div className={styles.switchContainer}>
                                    <MSwitch
                                        checked={settings.orderFlowSettings.announceProductOnAddToCart}
                                        onChange={() => {
                                            setSettings((prev) => ({
                                                ...prev,
                                                orderFlowSettings: {
                                                    ...prev.orderFlowSettings,
                                                    announceProductOnAddToCart:
                                                        !prev.orderFlowSettings.announceProductOnAddToCart,
                                                },
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div> */}
                </>
            )}

            {/* 展場推薦模式設定 - 只在展場推薦模式下顯示 */}
            {settings.chatMode === "featured_recommendations" && (
                <div className={styles.settingsSection}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                        {/* 知識類型 */}
                        <Box>
                            <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1.5 }}>
                                {t("SALES.KnowledgeTypes") || "知識類型"}
                            </Typography>
                            <p className={styles.sectionDescription}>
                                {t("SALES.KnowledgeTypesDescription") || "加入一般問答的標籤"}
                            </p>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <TextField
                                    value={knowledgeTypeInput}
                                    onChange={(e) => setKnowledgeTypeInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleAddKnowledgeType();
                                        }
                                    }}
                                    label={t("SALES.AddKnowledgeType") || "新增知識類型"}
                                    size='small'
                                    variant='standard'
                                    fullWidth
                                />
                                <IconButton size='small' onClick={handleAddKnowledgeType}>
                                    <AddIcon />
                                </IconButton>
                            </Box>
                            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                                {settings.featureRecommendationSettings.knowledgeTypes.map((type, index) => (
                                    <Chip
                                        key={`${type}-${index}`}
                                        label={type}
                                        size='small'
                                        onDelete={() => handleDeleteKnowledgeType(type)}
                                        deleteIcon={<CloseIcon fontSize='small' />}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        {/* 品項類型 */}
                        <Box>
                            <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1.5 }}>
                                {t("SALES.ItemTypes") || "品項類型"}
                            </Typography>
                            <p className={styles.sectionDescription}>
                                {t("SALES.ItemTypesDescription") || "加入推薦商品的標籤"}
                            </p>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <TextField
                                    value={itemTypeInput}
                                    onChange={(e) => setItemTypeInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleAddItemType();
                                        }
                                    }}
                                    label={t("SALES.AddItemType") || "新增品項類型"}
                                    size='small'
                                    variant='standard'
                                    fullWidth
                                />
                                <IconButton size='small' onClick={handleAddItemType}>
                                    <AddIcon />
                                </IconButton>
                            </Box>
                            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                                {settings.featureRecommendationSettings.itemTypes.map((type, index) => (
                                    <Chip
                                        key={`${type}-${index}`}
                                        label={type}
                                        size='small'
                                        onDelete={() => handleDeleteItemType(type)}
                                        deleteIcon={<CloseIcon fontSize='small' />}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    </Box>

                    {/* 精準推薦彈窗 */}
                    {/* <div className={styles.optionItem}>
                        <span className={styles.optionLabel}>
                            {t("SALES.UseAccurateKnowledgePopup") || "精準推薦彈窗"}
                        </span>
                        <div className={styles.switchContainer}>
                            <MSwitch
                                checked={settings.featureRecommendationSettings.useAccurateKnowledgePopup}
                                onChange={() => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        featureRecommendationSettings: {
                                            ...prev.featureRecommendationSettings,
                                            useAccurateKnowledgePopup:
                                                !prev.featureRecommendationSettings.useAccurateKnowledgePopup,
                                        },
                                    }));
                                }}
                            />
                        </div>
                    </div> */}

                    {/* 搜尋結果限制 */}
                    {/* <Box sx={{ mt: 3 }}>
                        <Typography variant='subtitle2' sx={{ mb: 2, fontWeight: 600 }}>
                            {t("SALES.SearchResultLimits") || "搜尋結果限制"}
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                                gap: 2,
                            }}
                        >
                            <MInput
                                label={t("SALES.EmbeddingSearchLimit") || "Embedding 搜尋"}
                                type='number'
                                value={settings.featureRecommendationSettings.embeddingSearchLimit}
                                onChange={(e) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        featureRecommendationSettings: {
                                            ...prev.featureRecommendationSettings,
                                            embeddingSearchLimit: Number(e.target.value),
                                        },
                                    }));
                                }}
                                variant='outlined'
                                required={false}
                            />
                            <MInput
                                label={t("SALES.FulltextSearchLimit") || "全文檢索"}
                                type='number'
                                value={settings.featureRecommendationSettings.fulltextSearchLimit}
                                onChange={(e) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        featureRecommendationSettings: {
                                            ...prev.featureRecommendationSettings,
                                            fulltextSearchLimit: Number(e.target.value),
                                        },
                                    }));
                                }}
                                variant='outlined'
                                required={false}
                            />
                            <MInput
                                label={t("SALES.TagRecommendLimit") || "標籤推薦"}
                                type='number'
                                value={settings.featureRecommendationSettings.tagrecommendLimit}
                                onChange={(e) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        featureRecommendationSettings: {
                                            ...prev.featureRecommendationSettings,
                                            tagrecommendLimit: Number(e.target.value),
                                        },
                                    }));
                                }}
                                variant='outlined'
                                required={false}
                            />
                            <MInput
                                label={t("SALES.EmbeddingSearchMinScore") || "Embedding 最低分數"}
                                type='number'
                                value={settings.featureRecommendationSettings.embeddingSearchMinScore}
                                onChange={(e) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        featureRecommendationSettings: {
                                            ...prev.featureRecommendationSettings,
                                            embeddingSearchMinScore: Number(e.target.value),
                                        },
                                    }));
                                }}
                                variant='outlined'
                                required={false}
                            />
                            <MInput
                                label={t("SALES.FulltextSearchMinScore") || "全文檢索最低分數"}
                                type='number'
                                value={settings.featureRecommendationSettings.fulltextSearchMinScore}
                                onChange={(e) => {
                                    setSettings((prev) => ({
                                        ...prev,
                                        featureRecommendationSettings: {
                                            ...prev.featureRecommendationSettings,
                                            fulltextSearchMinScore: Number(e.target.value),
                                        },
                                    }));
                                }}
                                variant='outlined'
                                required={false}
                            />
                        </Box>
                    </Box> */}
                </div>
            )}

            {/* 儲存按鈕 */}
            <div className={styles.saveButtonContainer}>
                <MButton
                    btnText={t("SALES.Save") || "Save Settings"}
                    handleClick={handleSave}
                    variant='contained'
                    color='primary'
                    disabled={isUpdatingDining || isUpdatingOrderFlow || isUpdatingFeatureRecommendation}
                />
            </div>
        </div>
    );
};

export default CardSettings;
